import type { FocusEventHandler, KeyboardEventHandler, Ref } from 'react'
import { Autocomplete } from '@base-ui/react/autocomplete'
import { Search } from 'lucide-react'

import { pluralize } from '@/lib/pluralize'
import { cn } from '@/lib/utils'
import { type EntityPathNode, type EntitySearchResult } from '@/modules/hierarchy/api/hierarchy-api'
import { Button } from '@/ui/button'
import { inputClassName } from '@/ui/input'

import { type EntityRef } from '../../lib/use-entity-recents'

function formatPath(path: EntityPathNode[]): string {
  const names = path.map((node) => node.name)
  if (names.length <= 3) return names.join(' → ')
  return `${names[0]} → … → ${names[names.length - 1]}`
}

const searchRowClass =
  'flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1 text-left text-xs text-foreground transition-colors hover:bg-muted data-[highlighted]:bg-muted focus-visible:outline-none data-[selected=true]:bg-primary/10 data-[selected=true]:font-medium data-[disabled]:cursor-default data-[disabled]:hover:bg-transparent'

export function searchStatusText(
  isSearching: boolean,
  pending: boolean,
  hasError: boolean,
  count: number,
): string {
  if (!isSearching || hasError) return ''
  if (pending) return 'Searching…'
  if (count === 0) return 'No entities found.'
  return `${pluralize(count, 'entity', 'entities')} found`
}

export function EntitySearchInput({
  query,
  onValueChange,
  autoFocus = false,
  label = 'Search entities',
  ref,
  onBlur,
  onFocus,
  ariaInvalid,
  ariaDescribedBy,
  placeholder = 'Search entities…',
  disabled = false,
  onKeyDown,
}: {
  query: string
  onValueChange: (value: string) => void
  autoFocus?: boolean
  label?: string
  ref?: Ref<HTMLInputElement>
  onBlur?: () => void
  onFocus?: FocusEventHandler<HTMLInputElement>
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  placeholder?: string
  disabled?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}) {
  return (
    <div className="relative w-full min-w-0">
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2" />
      <Autocomplete.Input
        ref={ref}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: opt-in autofocus when a caller opens this search in a popover (EP-6)
        autoFocus={autoFocus}
        aria-label={label}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(inputClassName, 'h-8 pl-7')}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={(e) => {
          onKeyDown?.(e)
          if (e.defaultPrevented) return
          if (e.key === 'Escape' && query !== '') {
            e.preventDefault()
            e.stopPropagation()
            onValueChange('')
          }
        }}
      />
    </div>
  )
}

export function EntitySearchResults({
  results,
  error,
  hasNextPage,
  selectedId,
  disabledId = null,
  blockedRootId = null,
  height = 'capped',
  onSelect,
  onRetry,
}: {
  results: EntitySearchResult[]
  error: Error | null
  hasNextPage: boolean
  selectedId: string | null
  disabledId?: string | null
  blockedRootId?: string | null
  height?: 'capped' | 'fill'
  onSelect: (ref: EntityRef) => void
  onRetry: () => void
}) {
  if (error) {
    return (
      <div role="alert" className="flex items-center gap-2 px-2 py-1">
        <span className="text-destructive text-xs">{error.message}</span>
        <Button type="button" variant="outline" size="xs" onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <>
      <Autocomplete.List
        aria-label="Search results"
        className={cn(
          'flex flex-col gap-0.5 overflow-y-auto',
          height === 'fill' ? 'min-h-0 grow' : 'max-h-72',
        )}
      >
        {results.map((result) => {
          const isSelected = result.entityId === selectedId
          const isBlocked =
            result.entityId === disabledId ||
            result.entityId === blockedRootId ||
            result.pathFromRoot.some((n) => n.entityId === blockedRootId)
          return (
            <Autocomplete.Item
              key={result.entityId}
              value={result.entityId}
              data-selected={isSelected}
              disabled={isSelected || isBlocked}
              onClick={() =>
                onSelect({ id: result.entityId, name: result.name })
              }
              className={cn(searchRowClass, isBlocked && 'text-muted-foreground')}
            >
              <span className="w-full truncate">{result.name}</span>
              {result.pathFromRoot.length > 0 && (
                <span className="text-muted-foreground w-full truncate text-[10px]">
                  {formatPath(result.pathFromRoot)}
                </span>
              )}
            </Autocomplete.Item>
          )
        })}
      </Autocomplete.List>
      {hasNextPage && (
        <p className="text-muted-foreground px-2 pt-1 text-[10px]">
          Showing the first {results.length} — refine your search to narrow it
          down.
        </p>
      )}
    </>
  )
}
